import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, UploadCloud, UserRound } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useHeaderStore } from '../../stores/headerStore';
import { StatusBar } from './StatusBar';
import { AccountControl } from './AccountControl';
import { WorkspaceSelector } from './WorkspaceSelector';
import { sessionHasEnded, subscribeSessionEnded } from '../../auth/sessionWatch';
import { bindWorkspaceDataSource, resetDataSource } from '../../data/dataSourceFactory';
import { useSelectedWorkspace } from '../../stores/workspaceStore';

/** Bordered box with two rows: age on top (hover reveals DOB), sex below. */
function AgeSexBox({ age, sex, birthDate }: { age?: string; sex?: string; birthDate?: string }) {
  if (!age && !sex) return null;
  const compactAge = age?.replace(/\s+/g, '');
  const ageRow = compactAge ? (
    birthDate ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="px-2 py-1">{compactAge}</span>
        </TooltipTrigger>
        <TooltipContent side="right">DOB {birthDate}</TooltipContent>
      </Tooltip>
    ) : (
      <span className="px-2 py-1">{compactAge}</span>
    )
  ) : null;
  return (
    <div className="flex flex-col items-stretch rounded-md border text-center text-[11px] font-medium leading-none">
      {ageRow}
      {age && sex && <div className="border-t" />}
      {sex && <span className="px-2 py-1">{sex}</span>}
    </div>
  );
}

/** Initials for a code/identifier, e.g. "HOSP-MRN" -> "HM", "Hospital" -> "HO". */
function initials(name: string): string {
  const parts = name.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** A rounded-rectangle badge (like the patient avatar, but square) showing the
 *  issuer's initials, with the full issuer revealed on hover. */
function IssuerBadge({ issuer }: { issuer: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex h-8 min-w-8 items-center justify-center rounded-md bg-primary/15 px-1.5 text-xs font-semibold text-primary">
          {initials(issuer)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">{issuer}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Said out loud when the session ends, because nothing else says it.
 *
 * The sign-in gate runs before React mounts and never runs again, so an expiry
 * an hour later used to show as an empty worklist, tiles that would not load,
 * and an upload page reporting failures — every symptom except the cause.
 *
 * It does NOT reload on its own. Somebody halfway through picking forty slides
 * should not lose the queue because a token aged out; the reload is offered and
 * they choose when. Modal, though, because every action behind it will fail.
 */
function SessionEnded(): JSX.Element | null {
  const [ended, setEnded] = useState(sessionHasEnded());
  useEffect(() => subscribeSessionEnded(setEnded), []);
  if (!ended) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-ended-title"
        className="w-full max-w-sm rounded-lg border bg-card p-5 shadow-lg"
      >
        {/* Says what is KNOWN, not why. All this saw was a 401, and expiry is
            only the likeliest cause — it fired during development for a client
            that was not sending the session cookie at all, and "your session
            expired" would have sent somebody hunting the wrong thing. */}
        <h2 id="session-ended-title" className="text-sm font-semibold text-foreground">
          You are no longer signed in
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          The server is refusing this session, most often because it expired. Anything on this
          page is stale until you sign in again.
        </p>
        <Button className="mt-4 w-full" onClick={() => window.location.reload()}>
          Sign in again
        </Button>
      </div>
    </div>
  );
}

/**
 * The pages, reading DICOMweb from the workspace the header selected.
 *
 * A StudyInstanceUID is unique only inside a workspace, and the root DICOMweb
 * mount answers for the caller's DEFAULT one. The worklist lists the selected
 * workspace, so opening a row, its thumbnail, or any study page reached from
 * here has to read that same workspace — bound once, before a page queries, and
 * rebound (with every DICOMweb cache key following, see dataSourceRoot) when the
 * selection changes.
 */
function WorkspaceOutlet() {
  const { current, ready } = useSelectedWorkspace();
  const ws = current?.public_id ?? null;
  const [bound, setBound] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (!ready) return;
    if (ws) bindWorkspaceDataSource(ws);
    else resetDataSource();
    setBound(ws);
    return () => resetDataSource();
  }, [ready, ws]);
  if (!ready || bound !== ws) return null;
  return <Outlet />;
}

export function AppLayout() {
  const { pathname } = useLocation();
  // A study's details editor is not the study: no patient banner over the form.
  const onDetails = /^\/studies\/[^/]+\/details$/.test(pathname);
  const onStudy = pathname.startsWith('/studies/') && !onDetails;
  const onUpload = pathname === '/upload';
  const onWorkspaces = pathname === '/workspaces';
  const onProfile = pathname === '/profile';
  // Everything that is not the worklist gets the back link and its own title.
  //
  // Keyed on the pages rather than on `pathname !== '/'` so a new route cannot
  // silently inherit the worklist's chrome — which is exactly what /profile did
  // when it was added and this list was not updated: no way back to the
  // studies, and the worklist's own title over somebody's account settings.
  // The safer default would be `pathname !== '/'`; this stays explicit because
  // the TITLE has to be chosen per page anyway, so a new route has to be named
  // here regardless and the list cannot quietly fall behind again.
  const away = onStudy || onDetails || onUpload || onWorkspaces || onProfile;
  /**
   * Whether there is anywhere for this person to upload TO.
   *
   * Asked of every workspace they are in, not of the selected one: the upload
   * page offers a destination picker, so somebody who edits one workspace and
   * only views another still has somewhere to go. It matches the set the
   * server builds — `targets`, which drops exactly the viewers (scope.ts
   * targetsFrom) — so the link is there when the page behind it will work.
   *
   * A viewer-only account was shown Upload, and the page it led to could only
   * tell them they had nowhere to put anything.
   */
  const { workspaces } = useSelectedWorkspace();
  const canUpload = workspaces.some((w) => w.role !== 'viewer');
  const patientName = useHeaderStore((s) => s.patientName);
  const patientId = useHeaderStore((s) => s.patientId);
  const patientSex = useHeaderStore((s) => s.patientSex);
  const patientAge = useHeaderStore((s) => s.patientAge);
  const patientBirthDate = useHeaderStore((s) => s.patientBirthDate);
  const patientIdIssuer = useHeaderStore((s) => s.patientIdIssuer);
  const accession = useHeaderStore((s) => s.accession);
  const studyDateTime = useHeaderStore((s) => s.studyDateTime);

  return (
    <div className="flex h-full flex-col">
      <SessionEnded />
      <header className="relative flex h-13 shrink-0 cursor-default items-center border-b px-3">
        {/* Left: back to list (any non-root page) */}
        {away && (
          <Link
            to="/"
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Studies
          </Link>
        )}

        {/* Center: patient (study view) or "List" (worklist) */}
        <div className="absolute left-1/2 flex -translate-x-1/2 items-center">
          {onStudy ? (
            patientName && (
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <UserRound className="h-4 w-4" />
                </span>
                <div className="leading-tight">
                  <div className="text-sm font-semibold">{patientName}</div>
                  {patientId && <div className="text-xs text-muted-foreground">{patientId}</div>}
                </div>
                <AgeSexBox age={patientAge} sex={patientSex} birthDate={patientBirthDate} />
              </div>
            )
          ) : (
            <span className="text-sm font-semibold">
              {onUpload
                ? 'Upload'
                : onDetails
                  ? 'Edit details'
                  : onWorkspaces
                    ? 'Workspaces'
                    : onProfile
                      ? 'Your account'
                      : 'List'}
            </span>
          )}
        </div>

        {/* Right, and there is only ONE of these now.
            It used to be two mutually exclusive blocks, each with its own
            ml-auto — the worklist's navigation, or a study's accession — which
            left nowhere for something that belongs on every page. The account
            goes last, so it sits in the same corner whatever you are looking
            at, and the page-specific items keep their order in front of it. */}
        <div className="ml-auto flex items-center gap-2.5">
          {/* WHICH WORKSPACE, on the two pages whose contents it decides: the
              study list and the upload page. Not on a study — you are looking at
              one slide by then, and changing what the LIST holds underneath it
              would either do nothing visible or silently revoke the thing on
              screen. Not on workspaces or the account, which are about the
              workspaces themselves rather than a view into one. */}
          {(!away || onUpload) && <WorkspaceSelector />}

          {/* Upload only. Workspaces moved into the account menu — it is where
              you change who can see your slides, which is an account concern
              rather than somewhere you move between while reading one. */}
          {!away && canUpload && (
            <Link
              to="/upload"
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <UploadCloud className="h-4 w-4" />
              Upload
            </Link>
          )}

          {onStudy && (patientIdIssuer || accession || studyDateTime) && (
            <>
              {patientIdIssuer && <IssuerBadge issuer={patientIdIssuer} />}
              {(accession || studyDateTime) && (
                <div className="leading-tight">
                  {accession && <div className="text-sm font-medium">{accession}</div>}
                  {studyDateTime && (
                    <div className="text-xs text-muted-foreground">{studyDateTime}</div>
                  )}
                </div>
              )}
            </>
          )}

          <AccountControl />
        </div>
      </header>
      <main className="min-h-0 flex-1">
        <WorkspaceOutlet />
      </main>
      {/* The study page brings the viewer's own bottom bar (theme, Shortcuts,
          colour profile), the same as the community's; the status bar stands
          under every other page. */}
      {!onStudy && <StatusBar />}
    </div>
  );
}
